import { CheckCircle, Eye, EyeOff, Image, Palette, Trash2, Type, X, Zap } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useEditorStore } from "@/store/editorStore";
import { sanitizeSvgMarkup } from "@/utils/svgSanitizer";
import { ColorPicker } from "./ColorPicker";

interface BulkEditPanelProps {
  onClose: () => void;
}

export const BulkEditPanel = ({ onClose }: BulkEditPanelProps) => {
  const {
    selectedHotspotIds,
    hotspots,
    bulkUpdateHotspots,
    bulkDeleteHotspots,
    clearHotspotSelection,
  } = useEditorStore();

  const [isUpdating, setIsUpdating] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [iconPopoverOpen, setIconPopoverOpen] = useState(false);
  const [emojiPopoverOpen, setEmojiPopoverOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("style");

  // Get selected hotspots
  const selectedHotspots = hotspots.filter((h) => selectedHotspotIds.includes(h.id));

  // Icon presets (same as single hotspot editing)
  const REMIX_ICON_PRESETS = [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M13 12V20H11V12H4L12 4L20 12H13Z"></path></svg>`,
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M13 12H20L12 20L4 12H11V4H13V12Z"></path></svg>`,
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12 13V20L4 12L12 4V11H20V13H12Z"></path></svg>`,
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12 13H4V11H12V4L20 12L12 20V13Z"></path></svg>`,
    `<svg viewBox='0 0 24 24' fill='currentColor'><rect x='11' y='5' width='2' height='14' rx='1'/><rect x='5' y='11' width='14' height='2' rx='1'/></svg>`,
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12 6C12.8284 6 13.5 5.32843 13.5 4.5C13.5 3.67157 12.8284 3 12 3C11.1716 3 10.5 3.67157 10.5 4.5C10.5 5.32843 11.1716 6 12 6ZM9 10H11V18H9V20H15V18H13V8H9V10Z"></path></svg>`,
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M18.364 17.364L12 23.7279L5.63604 17.364C2.12132 13.8492 2.12132 8.15076 5.63604 4.63604C9.15076 1.12132 14.8492 1.12132 18.364 4.63604C21.8787 8.15076 21.8787 13.8492 18.364 17.364ZM12 15C14.2091 15 16 13.2091 16 11C16 8.79086 14.2091 7 12 7C9.79086 7 8 8.79086 8 11C8 13.2091 9.79086 15 12 15ZM12 13C10.8954 13 10 12.1046 10 11C10 9.89543 10.8954 9 12 9C13.1046 9 14 9.89543 14 11C14 12.1046 13.1046 13 12 13Z"></path></svg>`,
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M5 2H19C19.5523 2 20 2.44772 20 3V22.1433C20 22.4194 19.7761 22.6434 19.5 22.6434C19.4061 22.6434 19.314 22.6168 19.2344 22.5669L12 18.0313L4.76559 22.5669C4.53163 22.7136 4.22306 22.6429 4.07637 22.4089C4.02647 22.3293 4 22.2373 4 22.1433V3C4 2.44772 4.44772 2 5 2ZM18 4H6V19.4324L12 15.6707L18 19.4324V4Z"></path></svg>`,
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M5 3H20C21.1046 3 22 3.89543 22 5V8C22 9.10457 21.1046 10 20 10H18V13C18 15.2091 16.2091 17 14 17H8C5.79086 17 4 15.2091 4 13V4C4 3.44772 4.44772 3 5 3ZM18 5V8H20V5H18ZM2 19H20V21H2V19Z"></path></svg>`,
  ];

  const EMOJI_PRESETS = [
    "ℹ︎",
    "✔️",
    "✅",
    "𐄂",
    "🟢",
    "🟠",
    "🔴",
    "⭐️",
    "🌟",
    "✨",
    "⚠️",
    "🏷️",
    "✉️",
    "🌳",
    "💡",
    "❗",
    "❓",
    "🚕",
    "🛴",
    "🚂",
    "🛑",
    "🧭",
    "🏠",
    "🗺️",
    "🖼️",
    "🎥",
    "🔗",
    "📝",
    "🔒",
    "🔓",
    "🛠️",
    "🧩",
    "🧑‍💻",
    "👋",
    "🕹️",
    "🎯",
    "🧭",
  ];

  // Common properties that can be bulk edited
  const [bulkUpdates, setBulkUpdates] = useState({
    visible: null as boolean | null,
    backgroundColor: "",
    iconOrSvg: "",
    name: "",
    color: "",
    fontColor: "",
  });

  const handleBulkUpdate = async (updates: Partial<typeof bulkUpdates>) => {
    if (selectedHotspotIds.length === 0) return;

    console.log("🔍 BulkEditPanel handleBulkUpdate called with:", updates);
    console.log("🔍 Selected hotspot IDs:", selectedHotspotIds);

    setIsUpdating(true);
    try {
      // Separate content updates from direct field updates
      const contentUpdates: Record<string, unknown> = {};
      const directUpdates: Record<string, unknown> = {};

      Object.entries(updates).forEach(([key, value]) => {
        if (value !== null && value !== "" && value !== undefined) {
          if (["backgroundColor", "iconOrSvg", "color", "fontColor"].includes(key)) {
            contentUpdates[key] = value;
          } else {
            directUpdates[key] = value;
          }
        }
      });

      console.log("🔍 Content updates:", contentUpdates);
      console.log("🔍 Direct updates:", directUpdates);

      // Prepare final updates
      const finalUpdates: Record<string, unknown> = { ...directUpdates };
      if (Object.keys(contentUpdates).length > 0) {
        // For bulk updates, we need to update each hotspot's content individually
        // since each hotspot might have different existing content
        console.log("🔍 Content updates will be applied to each hotspot individually");
        finalUpdates.content = contentUpdates;
        console.log("🔍 Content updates to apply:", contentUpdates);
      }

      if (Object.keys(finalUpdates).length > 0) {
        console.log("🔍 Calling bulkUpdateHotspots with:", finalUpdates);
        await bulkUpdateHotspots(selectedHotspotIds, finalUpdates);
        console.log("✅ Bulk update successful");
      } else {
        console.log("⚠️ No updates to apply");
      }
    } catch (error) {
      console.error("❌ Error bulk updating hotspots:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedHotspotIds.length === 0) return;

    setIsUpdating(true);
    try {
      await bulkDeleteHotspots(selectedHotspotIds);
      clearHotspotSelection();
      onClose();
    } catch (error) {
      console.error("Error bulk deleting hotspots:", error);
    } finally {
      setIsUpdating(false);
      setShowDeleteDialog(false);
    }
  };

  const handleVisibilityToggle = (visible: boolean) => {
    console.log("🔍 Visibility toggle - applying:", visible);
    handleBulkUpdate({ visible });
  };

  if (selectedHotspotIds.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center text-gray-400">
          <div className="text-4xl mb-4">⚙️</div>
          <h3 className="text-lg font-semibold mb-2">No Selection</h3>
          <p className="text-sm">Select hotspots to edit them in bulk</p>
        </div>
      </div>
    );
  }

  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-white">
            <CheckCircle size={20} />
            <h3 className="font-semibold">Bulk Edit ({selectedHotspotIds.length} hotspots)</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <X size={16} />
          </Button>
        </div>

        <Separator className="bg-gray-700" />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-gray-700">
            <TabsTrigger
              value="style"
              className="data-[state=active]:bg-gray-600 data-[state=active]:text-white text-gray-300"
            >
              <Palette size={16} className="mr-2" />
              Style
            </TabsTrigger>
            <TabsTrigger
              value="actions"
              className="data-[state=active]:bg-gray-600 data-[state=active]:text-white text-gray-300"
            >
              <Zap size={16} className="mr-2" />
              Actions
            </TabsTrigger>
          </TabsList>

          {/* Style Tab */}
          <TabsContent value="style" className="space-y-6 mt-4">
            {/* Basic Properties */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Name</Label>
                <Input
                  value={bulkUpdates.name}
                  onChange={(e) => setBulkUpdates((prev) => ({ ...prev, name: e.target.value }))}
                  onBlur={() => {
                    if (bulkUpdates.name) {
                      console.log("🔍 Name blur - applying:", bulkUpdates.name);
                      handleBulkUpdate({ name: bulkUpdates.name });
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.currentTarget.blur();
                    }
                  }}
                  placeholder="Enter new name"
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>

              {/* Visibility Controls */}
              <div className="space-y-2">
                <Label className="text-gray-300">Visibility</Label>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleVisibilityToggle(true)}
                    disabled={isUpdating}
                    className="flex-1"
                  >
                    <Eye size={16} className="mr-2" />
                    Show All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleVisibilityToggle(false)}
                    disabled={isUpdating}
                    className="flex-1"
                  >
                    <EyeOff size={16} className="mr-2" />
                    Hide All
                  </Button>
                </div>
              </div>

              {/* Background Color */}
              <ColorPicker
                value={bulkUpdates.backgroundColor || "#E3F7FF"}
                onChange={(backgroundColor) => {
                  setBulkUpdates((prev) => ({ ...prev, backgroundColor }));
                  console.log("🔍 Background color changed - applying:", backgroundColor);
                  handleBulkUpdate({ backgroundColor });
                }}
                label="Background Color"
              />

              {/* Icon/SVG */}
              <div className="space-y-2">
                <Label className="text-gray-300">Icon or SVG</Label>
                <div className="flex items-center space-x-2">
                  <Input
                    value={bulkUpdates.iconOrSvg}
                    onChange={(e) =>
                      setBulkUpdates((prev) => ({ ...prev, iconOrSvg: e.target.value }))
                    }
                    onBlur={() => {
                      if (bulkUpdates.iconOrSvg) {
                        console.log("🔍 Icon blur - applying:", bulkUpdates.iconOrSvg);
                        handleBulkUpdate({ iconOrSvg: bulkUpdates.iconOrSvg });
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.currentTarget.blur();
                      }
                    }}
                    placeholder="Enter a single emoji/character, <path .../>, or <svg>...</svg>"
                    className="bg-gray-700 border-gray-600 text-white h-10 px-3"
                  />
                  <Popover open={iconPopoverOpen} onOpenChange={setIconPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="h-10 w-10 p-0 flex items-center justify-center bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                        tabIndex={-1}
                        type="button"
                      >
                        <span className="text-lg">★</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-64 p-4 bg-gray-800 border-gray-700"
                      onOpenAutoFocus={(e) => e.preventDefault()}
                    >
                      <div className="grid grid-cols-4 gap-2">
                        {REMIX_ICON_PRESETS.map((icon, _idx) => {
                          const sanitizedIcon = sanitizeSvgMarkup(icon);
                          if (!sanitizedIcon) {
                            return null;
                          }
                          return (
                            <button
                              key={icon}
                              className="w-12 h-12 flex items-center justify-center rounded border border-gray-600 bg-gray-700 hover:bg-gray-600 text-white"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setBulkUpdates((prev) => ({ ...prev, iconOrSvg: icon }));
                                console.log("🔍 Icon selected - applying:", icon);
                                handleBulkUpdate({ iconOrSvg: icon });
                              }}
                              type="button"
                              tabIndex={-1}
                              // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized preset SVG
                              dangerouslySetInnerHTML={{ __html: sanitizedIcon }}
                            />
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                  <Popover open={emojiPopoverOpen} onOpenChange={setEmojiPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="h-10 w-10 p-0 flex items-center justify-center bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                        tabIndex={-1}
                        type="button"
                      >
                        <span className="text-lg">😊</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-64 p-4 bg-gray-800 border-gray-700"
                      onOpenAutoFocus={(e) => e.preventDefault()}
                    >
                      <div className="grid grid-cols-8 gap-2">
                        {EMOJI_PRESETS.map((emoji) => (
                          <button
                            key={emoji}
                            className="w-8 h-8 flex items-center justify-center rounded border border-gray-600 bg-gray-700 hover:bg-gray-600 text-lg"
                            onClick={() => {
                              setBulkUpdates((prev) => ({ ...prev, iconOrSvg: emoji }));
                              console.log("🔍 Emoji selected - applying:", emoji);
                              handleBulkUpdate({ iconOrSvg: emoji });
                            }}
                            type="button"
                            tabIndex={-1}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Font Color */}
              <ColorPicker
                value={bulkUpdates.fontColor || "#ffffff"}
                onChange={(fontColor) => {
                  setBulkUpdates((prev) => ({ ...prev, fontColor }));
                  console.log("🔍 Font color changed - applying:", fontColor);
                  handleBulkUpdate({ fontColor });
                }}
                label="Font Color"
              />
            </div>
          </TabsContent>

          {/* Actions Tab */}
          <TabsContent value="actions" className="space-y-6 mt-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Selected Hotspots</Label>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {selectedHotspots.map((hotspot) => (
                    <div
                      key={hotspot.id}
                      className="flex items-center space-x-2 p-2 bg-gray-700 rounded text-sm"
                    >
                      {hotspot.type === "text" ? (
                        <Type size={14} className="text-white" />
                      ) : (
                        <Image size={14} className="text-white" />
                      )}
                      <span className="text-gray-300 truncate">{hotspot.name}</span>
                      <span className="text-gray-500 text-xs">
                        {hotspot.visible ? "Visible" : "Hidden"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <Separator className="bg-gray-700" />

              {/* Danger Zone */}
              <div className="space-y-3">
                <Label className="text-red-400">Danger Zone</Label>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowDeleteDialog(true)}
                  disabled={isUpdating}
                  className="w-full"
                >
                  <Trash2 size={16} className="mr-2" />
                  Delete All Selected
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Delete Confirmation Dialog */}
        {showDeleteDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 max-w-sm w-full mx-4">
              <h3 className="text-white font-semibold mb-2">Delete Hotspots?</h3>
              <p className="text-gray-300 mb-4">
                Are you sure you want to delete {selectedHotspotIds.length} hotspots? This action
                cannot be undone.
              </p>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteDialog(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleBulkDelete}
                  disabled={isUpdating}
                  className="flex-1"
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ScrollArea>
  );
};
